import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractErrorCode } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { Image as ImageIcon, Loader2, Download, Trash2, Upload, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  { id: "gemini-nano-banana", labelKey: "images.provider_gemini" },
  { id: "openai-gpt-image-1", labelKey: "images.provider_openai" },
];

// Max upload size on the encoded base64 string (decoded ≈ 8 MB).
const MAX_REFERENCE_DATAURL_LEN = 11 * 1024 * 1024;
const ALLOWED_REFERENCE_TYPES = ["image/png", "image/jpeg", "image/webp"];

export default function ImageStudio() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("gemini-nano-banana");
  const [generating, setGenerating] = useState(false);
  const [images, setImages] = useState([]);
  const [referenceDataUrl, setReferenceDataUrl] = useState(null); // data:image/...;base64,...
  const fileInputRef = useRef(null);

  const loadGallery = async () => {
    const r = await api.get("/images/gallery");
    setImages(r.data.images || []);
  };

  useImperativeLoad(() => { loadGallery(); }, []);

  const handleReferencePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_REFERENCE_TYPES.includes(file.type)) {
      toast.error(t("images.err_REFERENCE_INVALID_TYPE"));
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      if (dataUrl.length > MAX_REFERENCE_DATAURL_LEN) {
        toast.error(t("images.err_IMAGE_REFERENCE_TOO_LARGE"));
        return;
      }
      setReferenceDataUrl(dataUrl);
      // Reference is Gemini-only — auto-switch the radio to make the UX honest.
      if (provider !== "gemini-nano-banana") {
        setProvider("gemini-nano-banana");
        toast.info(t("images.reference_forced_gemini"));
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const clearReference = () => {
    setReferenceDataUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || generating) return;
    setGenerating(true);
    try {
      const body = {
        prompt: prompt.trim(),
        provider,
      };
      if (referenceDataUrl) body.reference_image_b64 = referenceDataUrl;
      const r = await api.post("/images/generate", body);
      setImages((imgs) => [r.data.image, ...imgs]);
      refresh();
      toast.success(referenceDataUrl ? t("images.edit_success") : t("images.gen_success"));
      setPrompt("");
      // Keep reference for follow-up edits; user can clear it explicitly.
    } catch (err) {
      const code = extractErrorCode(err);
      const translated = t(`images.err_${code}`);
      const msg = typeof translated === "string" && !translated.startsWith("images.") ? translated : `Error: ${code}`;
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (img) => {
    const a = document.createElement("a");
    a.href = img.image_data_url;
    a.download = `neuracom-${img.image_id}.png`;
    a.click();
  };

  const handleDelete = async (id) => {
    await api.delete(`/images/${id}`);
    setImages((imgs) => imgs.filter((i) => i.image_id !== id));
  };

  const isEditMode = !!referenceDataUrl;

  return (
    <div className="px-8 py-10 lg:px-12" data-testid="image-studio-page">
      <header className="mb-8">
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-white">
          {t("images.title")}
        </h1>
      </header>

      <Card className="mb-10 border-slate-800 bg-[#0A0C1F] p-6">
        {/* Reference image (optional) */}
        <div className="mb-6">
          <Label className="text-sm font-medium text-slate-300">
            {t("images.reference_label")}
          </Label>
          {referenceDataUrl ? (
            <div
              className="mt-2 flex items-start gap-4 rounded-xl border border-[#9ACD32]/30 bg-[#9ACD32]/5 p-3"
              data-testid="image-reference-preview"
            >
              <img
                src={referenceDataUrl}
                alt={t("images.reference_label")}
                className="h-24 w-24 shrink-0 rounded-lg object-cover ring-1 ring-slate-700"
              />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#9ACD32]">
                  <Sparkles className="h-3 w-3" />
                  {t("images.reference_active")}
                </p>
                <p className="mt-1 text-xs text-slate-400">{t("images.reference_hint")}</p>
              </div>
              <Button
                onClick={clearReference}
                size="icon"
                variant="ghost"
                className="h-8 w-8 shrink-0 rounded-full text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                data-testid="image-reference-clear-btn"
                aria-label="clear reference"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <label
              htmlFor="reference-upload"
              className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed border-slate-700 bg-slate-900/40 p-4 text-sm text-slate-400 transition-colors hover:border-[#9ACD32]/50 hover:bg-[#9ACD32]/5 hover:text-slate-200"
              data-testid="image-reference-upload-label"
            >
              <Upload className="h-4 w-4 shrink-0 text-[#9ACD32]" />
              <span>{t("images.reference_pick")}</span>
              <span className="ml-auto text-xs text-slate-500">{t("images.reference_size_hint")}</span>
              <input
                id="reference-upload"
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleReferencePick}
                className="hidden"
                data-testid="image-reference-input"
              />
            </label>
          )}
        </div>

        <Label className="text-sm font-medium text-slate-300">
          {isEditMode ? t("images.prompt_label_edit") : t("images.prompt_label")}
        </Label>
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={isEditMode ? t("images.prompt_placeholder_edit") : t("images.prompt_placeholder")}
          rows={3}
          className="mt-2 resize-none border-slate-700 bg-slate-900 text-[15px]"
          disabled={generating}
          data-testid="image-prompt-input"
        />

        <div className="mt-5">
          <Label className="text-xs uppercase tracking-wider text-slate-400">
            {t("images.provider_label")}
          </Label>
          <RadioGroup
            value={provider}
            onValueChange={(v) => {
              if (isEditMode && v !== "gemini-nano-banana") {
                toast.info(t("images.reference_forced_gemini"));
                return;
              }
              setProvider(v);
            }}
            className="mt-3 flex gap-3"
            disabled={generating}
          >
            {PROVIDERS.map((p) => {
              const disabled = isEditMode && p.id !== "gemini-nano-banana";
              return (
                <label
                  key={p.id}
                  htmlFor={`provider-${p.id}`}
                  className={`flex flex-1 items-center gap-3 rounded-xl border-2 p-4 transition-colors ${
                    provider === p.id
                      ? "border-[#9ACD32] bg-[#9ACD32]/10"
                      : "border-slate-800 hover:border-slate-700"
                  } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                  data-testid={`image-provider-${p.id}`}
                >
                  <RadioGroupItem
                    value={p.id}
                    id={`provider-${p.id}`}
                    className="border-slate-600 text-[#9ACD32]"
                    disabled={disabled}
                  />
                  <span className="text-sm font-semibold text-white">{t(p.labelKey)}</span>
                  {disabled && (
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-500">
                      {t("images.no_reference_support")}
                    </span>
                  )}
                </label>
              );
            })}
          </RadioGroup>
        </div>

        <Button
          onClick={handleGenerate}
          disabled={!prompt.trim() || generating}
          className="lime-glow mt-6 h-12 rounded-full bg-[#9ACD32] px-8 font-bold text-[#05073B] hover:bg-[#A8DF38]"
          data-testid="image-generate-btn"
        >
          {generating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("images.generating")}
            </>
          ) : (
            <>
              <ImageIcon className="mr-2 h-4 w-4" />
              {isEditMode ? t("images.edit_btn") : t("images.generate_btn")}
            </>
          )}
        </Button>
      </Card>

      <section>
        <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("images.gallery")}
        </h2>
        {images.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="image-empty-state">{t("images.no_images")}</p>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {images.map((img) => (
              <Card key={img.image_id} className="group relative overflow-hidden border-slate-800 bg-[#0A0C1F] p-0" data-testid={`image-card-${img.image_id}`}>
                <img src={img.image_data_url} alt={img.prompt} loading="lazy" decoding="async" className="aspect-square w-full object-cover" />
                {img.has_reference && (
                  <span
                    className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-[#9ACD32]/90 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#05073B] shadow"
                    data-testid={`image-card-edit-badge-${img.image_id}`}
                  >
                    <Sparkles className="h-2.5 w-2.5" />
                    {t("images.edit_badge")}
                  </span>
                )}
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/90 via-black/40 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="w-full">
                    <p className="line-clamp-2 text-[11px] text-white">{img.prompt}</p>
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleDownload(img)}
                        className="h-7 flex-1 rounded-full text-xs"
                        data-testid={`image-download-${img.image_id}`}
                      >
                        <Download className="mr-1 h-3 w-3" />
                        {t("images.download")}
                      </Button>
                      <Button
                        size="icon"
                        variant="destructive"
                        onClick={() => handleDelete(img.image_id)}
                        className="h-7 w-7 rounded-full"
                        data-testid={`image-delete-${img.image_id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
